import { useNavegacao } from "@/lib/navegacao";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { useLocation } from "wouter";
import { getISOWeek, getISOWeekYear } from "date-fns";
import type { Variedade, Ciclo as BaseCiclo, Mangara, Colheita } from "@shared/schema";
import { useSetores } from "@/hooks/use-setores";
import { useVariedades, useVariedadesDoCacho } from "@/hooks/use-variedades-cacho";
import { PageHeader } from "@/components/layout/page-header";
import { PageToolbar } from "@/components/layout/page-toolbar";

import { PageSkeleton } from "@/components/data/page-skeleton";
import { EmptyState } from "@/components/data/empty-state";
import { AvisoNovoComprador } from "@/components/mercado/aviso-novo-comprador";
type Ciclo = BaseCiclo & {
  dataPrevisaoInicio: Date | null;
  dataPrevisaoFim: Date | null;
};

// Função para obter semana ISO 8601 atual
function getCurrentISOWeek(): { year: number; week: number } {
  const now = new Date();
  return {
    year: getISOWeekYear(now),
    week: getISOWeek(now)
  };
}

// Função para determinar se uma cor é clara ou escura (baseado em luminância)
function isColorDark(hexColor: string): boolean {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Fórmula de luminância relativa
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

// Helper para status do ciclo - Mesmo sistema usado em Controle de Fitas
// Finalizado (cinza): ciclo.status === FECHADO_MANUAL
// Finalizar Ciclo (vermelho): hoje > dataPrevisaoFim
// Em Andamento (verde): hoje entre dataPrevisaoInicio e dataPrevisaoFim
// Próximo (azul): hoje < dataPrevisaoInicio
const getCicloStatus = (ciclo: Ciclo): { label: string; variant: "default" | "secondary" | "destructive" | "outline"; className?: string } => {
  // Status Finalizado - Ciclo fechado manualmente
  if (ciclo.status === "FECHADO" || ciclo.status === "FECHADO_MANUAL") {
    return { 
      label: "Finalizado", 
      variant: "outline",
      className: "bg-muted/50 text-muted-foreground border-muted-foreground/20"
    };
  }

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  
  const dataPrevisaoInicio = ciclo.dataPrevisaoInicio ? new Date(ciclo.dataPrevisaoInicio) : null;
  const dataPrevisaoFim = ciclo.dataPrevisaoFim ? new Date(ciclo.dataPrevisaoFim) : null;
  
  if (dataPrevisaoInicio) dataPrevisaoInicio.setHours(0, 0, 0, 0);
  if (dataPrevisaoFim) dataPrevisaoFim.setHours(0, 0, 0, 0);
  
  // Finalizar Ciclo (vermelho) - Período de colheita terminou
  if (dataPrevisaoFim && hoje > dataPrevisaoFim) {
    return { 
      label: "Finalizar Ciclo", 
      variant: "destructive",
      className: "bg-destructive/10 text-destructive border-destructive/20"
    };
  }
  
  // Em Andamento (verde) - Dentro do período de colheita
  if (dataPrevisaoInicio && dataPrevisaoFim && hoje >= dataPrevisaoInicio && hoje <= dataPrevisaoFim) {
    return { 
      label: "Em Andamento", 
      variant: "default",
      className: "bg-sucesso-suave text-sucesso border-sucesso/20"
    };
  }
  
  // Próximo (azul) - Antes do período de colheita
  return { 
    label: "Próximo", 
    variant: "default",
    className: "bg-primary/10 text-primary border-primary/20"
  };
};

// Função para ordenar ciclos por data de previsão
const sortCiclosByPrevisao = (ciclos: Ciclo[]) => {
  return [...ciclos].sort((a, b) => {
    const dateA = a.dataPrevisaoInicio ? new Date(a.dataPrevisaoInicio).getTime() : 0;
    const dateB = b.dataPrevisaoInicio ? new Date(b.dataPrevisaoInicio).getTime() : 0;
    return dateA - dateB;
  });
};

// Helper para extrair informações da fita para exibição
const getFitaInfo = (ciclo: Ciclo, mangaras?: Mangara[], coresFitas?: any[], setores?: any[], variedades?: any[], selectedVariedadeId?: string) => {
  const mangarasCiclo = mangaras?.filter(m => m.cicloId === ciclo.id) || [];
  
  // Filtrar mangarás pela variedade selecionada
  let mangarasFiltered = mangarasCiclo;
  if (selectedVariedadeId && selectedVariedadeId !== "todas") {
    mangarasFiltered = mangarasCiclo.filter(m => {
      const setor = setores?.find(s => s.id === m.setorId);
      return setor?.variedadeId === selectedVariedadeId;
    });
  }
  
  const firstMangara = mangarasFiltered.sort((a, b) => 
    (a.semanaCorteMangara || 0) - (b.semanaCorteMangara || 0)
  )[0];
  
  const corFita = coresFitas?.find(c => c.id === ciclo.corFitaId);
  const setor = setores?.find(s => s.id === firstMangara?.setorId);
  const variedade = variedades?.find(v => v.id === setor?.variedadeId);
  
  return {
    corNome: corFita?.nome || 'N/A',
    hexColor: corFita?.hexColor || '#000',
    variedadeNome: variedade?.nome || 'N/A',
    mangaraWeek: firstMangara?.semanaCorteMangara || null,
    harvestWeek: firstMangara?.semanaPrevistaColheita || null,
    harvestDate: firstMangara?.dataPrevistaColheita || null,
  };
};

// Helper para filtrar ciclos por variedade selecionada
const getCiclosByVariedade = (ciclos: Ciclo[], variedadeId: string, mangaras?: Mangara[], setores?: any[]) => {
  if (variedadeId === "todas") {
    return ciclos;
  }
  
  return ciclos.filter(ciclo => {
    const cicloMangaras = mangaras?.filter(m => m.cicloId === ciclo.id) || [];
    return cicloMangaras.some(m => {
      const setor = setores?.find(s => s.id === m.setorId);
      return setor?.variedadeId === variedadeId;
    });
  });
};

export default function InicioPage() {
  const [, setLocation] = useLocation();
  // Abrir um ciclo daqui grava o Início como origem: o "Voltar" da análise
  // devolve para cá, não para /cacho/ciclos.
  const { abrir } = useNavegacao();
  const [variedadeId, setVariedadeId] = useState<string>("todas");
  const [gridStartIndex, setGridStartIndex] = useState<number>(0);

  // `variedades` alimenta os DOIS seletores desta tela (topo e Modo TV) → só
  // banana. `todasAsVariedades` é o LOOKUP do nome da variedade de um setor,
  // em `getFitaInfo` — lista completa, senão setor de outra cultura fica sem
  // rótulo.
  const { variedades } = useVariedadesDoCacho();
  const { data: todasAsVariedades } = useVariedades();

  const { setores } = useSetores({ somenteProdutivos: true });

  // Ciclos sempre sem filtro - para "Fitas Abertas" mostrar TODOS os ciclos ativos
  const { data: ciclos, isLoading: ciclosLoading } = useQuery<Ciclo[]>({
    queryKey: ["/api/ciclos"],
  });

  const { data: mangaras } = useQuery<Mangara[]>({
    queryKey: ["/api/mangaras"],
  });

  const { data: colheitas } = useQuery<Colheita[]>({
    queryKey: ["/api/colheitas"],
  });

  const { data: coresFitas } = useQuery<any[]>({
    queryKey: ["/api/cores"],
  });

  type ColorData = { corNome: string; corHex: string; planejado: number };
  type WeekData = { cores: ColorData[]; ano: number; semana: number };
  type SetorData = { id: string; nome: string; weeks: Record<string, WeekData> };
  
  // Filtro apenas para tabela de previsão semanal
  const queryParams = variedadeId !== "todas" ? `?variedadeId=${variedadeId}` : "";
  const { data: previsao, isLoading } = useQuery<{
    setores: Record<string, SetorData>;
    totais: Record<string, WeekData>;
  }>({
    queryKey: [`/api/previsao-semanal${queryParams}`],
  });

  // Reset grid when variety changes
  useEffect(() => {
    setGridStartIndex(0);
  }, [variedadeId]);

  if (isLoading) {
    return <div className="p-6"><PageSkeleton variante="painel" /></div>;
  }

  if (!previsao || !previsao.setores) {
    return (
      <div className="p-6">
        <EmptyState
          titulo="Nenhum dado disponível"
          descricao="A previsão sai dos mangarás cortados — registre o primeiro corte para a semana começar a aparecer aqui."
        />
      </div>
    );
  }

  // Get current week
  const currentWeek = getCurrentISOWeek();
  const currentWeekKey = `${currentWeek.year}-S${currentWeek.week}`;
  
  // Get all unique weeks sorted
  const allWeeks = Array.from(
    new Set(
      Object.values(previsao.setores).flatMap(setor => Object.keys(setor.weeks))
    )
  ).sort((a, b) => {
    const [yearA, weekA] = a.split('-S').map(s => parseInt(s));
    const [yearB, weekB] = b.split('-S').map(s => parseInt(s));
    return yearA === yearB ? weekA - weekB : yearA - yearB;
  });

  // If no weeks available, show message
  if (allWeeks.length === 0) {
    return (
      <div className="p-6">
        <div className="space-y-4 mb-6">
          <PageHeader
            titulo="Previsão de colheita"
            descricao="Visualização por semana e setor"
          />
          <PageToolbar>
            <Select value={variedadeId} onValueChange={setVariedadeId}>
              <SelectTrigger className="w-[200px]" data-testid="select-variedade">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                {variedades?.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </PageToolbar>
        </div>
        <div className="text-center p-12 text-muted-foreground">
          Nenhum dado disponível para a variedade selecionada
        </div>
      </div>
    );
  }

  // Get weeks for top cards: 5 cards (2 before, current, 2 after) for both mobile and desktop
  const currentIndex = allWeeks.findIndex(w => w === currentWeekKey);
  
  // Find the closest week to current week when current week is not in the data
  const findClosestWeekIndex = () => {
    if (currentIndex >= 0) return currentIndex;
    
    // Parse current week
    const [currentYear, currentWeekNum] = [currentWeek.year, currentWeek.week];
    
    // Find the closest week by comparing year and week
    let closestIndex = 0;
    let closestDistance = Infinity;
    
    allWeeks.forEach((weekKey, idx) => {
      const [year, week] = weekKey.split('-S').map(s => parseInt(s));
      // Distance in weeks (approximate)
      const distance = Math.abs((year - currentYear) * 52 + (week - currentWeekNum));
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = idx;
      }
    });
    
    return closestIndex;
  };
  
  const effectiveIndex = findClosestWeekIndex();
  
  // For both mobile and desktop: 5 cards centered on effective index
  const topWeeks = allWeeks.slice(
    Math.max(0, effectiveIndex - 2), 
    Math.min(allWeeks.length, effectiveIndex + 3)
  );
  
  // All weeks for scrollable view on mobile
  const scrollableWeeks = allWeeks;

  // Calculate grid start index: center on closest week when gridStartIndex is 0
  let finalGridStartIndex = gridStartIndex;
  if (gridStartIndex === 0) {
    finalGridStartIndex = Math.max(0, Math.min(effectiveIndex - 2, allWeeks.length - 5));
  }
  
  // Filter weeks for grid: 5 weeks starting from finalGridStartIndex
  const gridWeeks = allWeeks.slice(finalGridStartIndex, finalGridStartIndex + 5);
  
  // Calculate navigation boundaries
  const canGoLeft = finalGridStartIndex > 0;
  const canGoRight = finalGridStartIndex + 5 < allWeeks.length;

  const handlePreviousWeeks = () => {
    setGridStartIndex(Math.max(0, finalGridStartIndex - 1));
  };

  const handleNextWeeks = () => {
    if (finalGridStartIndex + 5 < allWeeks.length) {
      setGridStartIndex(finalGridStartIndex + 1);
    }
  };

  const setoresPrevisao = Object.values(previsao.setores).sort((a, b) => a.nome.localeCompare(b.nome));

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Controle compensatório do claim-by-token do Mercado (C2): o token prova posse
          do LINK, não do número. Quem reconhece o nome — ou estranha — é o produtor,
          e a home é onde ele passa. Não renderiza nada quando não há aviso. */}
      <AvisoNovoComprador />

      <PageHeader
        titulo="Previsão de colheita"
        descricao="Visualização por semana e setor"
      />

      <PageToolbar>
        <Select value={variedadeId} onValueChange={setVariedadeId}>
          <SelectTrigger className="w-[160px]" data-testid="select-variedade">
            <SelectValue placeholder="Selecione" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas</SelectItem>
            {variedades?.map((v) => (
              <SelectItem key={v.id} value={v.id}>
                {v.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PageToolbar>

      {/* Top Cards - 5 cards: 2 before, current (highlighted), 2 after */}
      
      {/* Desktop: 5 cards centered with reduced spacing */}
      <div className="hidden md:flex justify-center items-end gap-2">
        {topWeeks.map((weekKey) => {
          if (!weekKey) return null;
          const total = previsao?.totais[weekKey];
          const [ano, semana] = weekKey.split('-S');
          const totalCachos = total?.cores.reduce((sum, c) => sum + c.planejado, 0) || 0;
          const dominantColor = total?.cores[0];
          const isCurrent = weekKey === currentWeekKey;
          
          return (
            <div key={weekKey} className="flex flex-col items-center" data-testid={`card-week-${weekKey}`}>
              <div className="text-center mb-2">
                <div className={`font-bold ${isCurrent ? 'text-2xl' : 'text-lg'}`}>
                  S{semana}
                </div>
              </div>
              <div 
                className={`flex items-center justify-center rounded-sm ${
                  isCurrent ? 'w-48 h-48' : 'w-36 h-36'
                } transition-all ${isCurrent ? 'ring-4 ring-primary shadow-lg' : ''}`}
                style={{ 
                  backgroundColor: dominantColor?.corHex || '#9CA3AF',
                  color: dominantColor?.corHex && isColorDark(dominantColor.corHex) ? '#FFFFFF' : '#000000'
                }}
              >
                <div className="text-center font-bold uppercase px-2" style={{
                  fontSize: isCurrent ? '2rem' : '1.5rem'
                }}>
                  {dominantColor?.corNome || 'SEM FITA'}
                </div>
              </div>
              <div className={`text-center mt-2 font-semibold ${isCurrent ? 'text-lg' : 'text-base'}`}>
                {totalCachos} cachos
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile: 5 main cards (2 before, current, 2 after) with reduced spacing */}
      <div className="md:hidden flex justify-center items-end gap-1 px-1">
        {topWeeks.map((weekKey) => {
          if (!weekKey) return null;
          const total = previsao?.totais[weekKey];
          const [ano, semana] = weekKey.split('-S');
          const totalCachos = total?.cores.reduce((sum, c) => sum + c.planejado, 0) || 0;
          const dominantColor = total?.cores[0];
          const isCurrent = weekKey === currentWeekKey;
          
          return (
            <div key={weekKey} className="flex flex-col items-center flex-1 min-w-0" data-testid={`card-week-${weekKey}`}>
              <div className="text-center mb-0.5">
                <div className={`font-bold ${isCurrent ? 'text-sm' : 'text-xs'}`}>
                  S{semana}
                </div>
              </div>
              <div 
                className={`flex items-center justify-center rounded-sm flex-shrink-0 ${
                  isCurrent ? 'w-20 h-20' : 'w-14 h-14'
                } transition-all ${isCurrent ? 'ring-2 ring-primary shadow-md' : ''}`}
                style={{ 
                  backgroundColor: dominantColor?.corHex || '#9CA3AF',
                  color: dominantColor?.corHex && isColorDark(dominantColor.corHex) ? '#FFFFFF' : '#000000'
                }}
              >
                <div className="text-center font-bold uppercase px-0.5" style={{
                  fontSize: isCurrent ? '0.75rem' : '0.55rem'
                }}>
                  {dominantColor?.corNome || 'SEM FITA'}
                </div>
              </div>
              <div className={`text-center mt-0.5 font-semibold text-xs`}>
                {totalCachos}
              </div>
            </div>
          );
        })}
      </div>

      {/* Grid - Setores x Semanas (with fixed column for sectors) */}
      <Card>
        <CardContent className="p-0">
          {/* Navigation Controls */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePreviousWeeks}
              disabled={!canGoLeft}
              data-testid="button-prev-weeks"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="text-sm font-semibold">
              {gridWeeks.length > 0 && (
                <span>
                  S{gridWeeks[0]?.split('-S')[1]} até S{gridWeeks[gridWeeks.length - 1]?.split('-S')[1]}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNextWeeks}
              disabled={!canGoRight}
              data-testid="button-next-weeks"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              <table className="w-full border-collapse" data-testid="table-previsao">
                <thead>
                  <tr>
                    <th className="border p-1 bg-muted text-left sticky left-0 z-20 bg-background min-w-[60px]">
                      Setor
                    </th>
                    {gridWeeks.map((weekKey) => {
                      const [ano, semana] = weekKey.split('-S');
                      const isCurrent = weekKey === currentWeekKey;
                      const dominantColor = previsao?.totais[weekKey]?.cores[0];
                      
                      return (
                        <th 
                          key={weekKey}
                          style={{
                            ...(dominantColor ? {
                              backgroundColor: `${dominantColor.corHex}33` // 20% opacity
                            } : {}),
                            ...(isCurrent && dominantColor ? {
                              borderColor: dominantColor.corHex,
                              borderWidth: '2px'
                            } : {})
                          }}
                          className={`border p-1 text-center min-w-[65px] font-bold ${isCurrent && !dominantColor ? '' : dominantColor ? '' : 'bg-muted'}`}
                        >
                          <div className="text-xs">S{semana}</div>
                          <div className="text-xs text-muted-foreground">{ano}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {setoresPrevisao.map((setor, setorIdx) => (
                    <tr key={setor.id} data-testid={`row-setor-${setor.nome}`}>
                      <td className="border p-1 font-medium sticky left-0 bg-background z-10 min-w-[60px] text-xs">{setor.nome}</td>
                      {gridWeeks.map((week) => {
                        const cell = setor.weeks[week];
                        const totalPlanejado = cell?.cores.reduce((sum: number, c: any) => sum + c.planejado, 0) || 0;
                        const isCurrent = week === currentWeekKey;
                        const dominantColor = cell?.cores[0];
                        
                        return (
                          <td
                            key={week}
                            style={{
                              ...(dominantColor ? {
                                backgroundColor: `${dominantColor.corHex}33` // 20% opacity
                              } : {}),
                              ...(isCurrent && dominantColor ? {
                                borderLeftColor: dominantColor.corHex,
                                borderRightColor: dominantColor.corHex,
                                borderLeftWidth: '2px',
                                borderRightWidth: '2px'
                              } : {})
                            }}
                            className={`border p-1 text-center text-xs`}
                            data-testid={`cell-${setor.nome}-${week}`}
                          >
                            {cell && totalPlanejado > 0 ? (
                              <div className="text-xs font-semibold leading-tight">
                                {totalPlanejado}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="border p-1 bg-muted font-bold sticky left-0 z-10 bg-muted text-xs">Total</td>
                    {gridWeeks.map((week) => {
                      const total = previsao?.totais[week];
                      const totalPlanejado = total?.cores.reduce((sum, c) => sum + c.planejado, 0) || 0;
                      const isCurrent = week === currentWeekKey;
                      const dominantColor = total?.cores[0];
                      
                      return (
                        <td
                          key={week}
                          style={{
                            ...(dominantColor ? {
                              backgroundColor: `${dominantColor.corHex}33` // 20% opacity
                            } : {}),
                            ...(isCurrent && dominantColor ? {
                              borderColor: dominantColor.corHex,
                              borderWidth: '2px'
                            } : {})
                          }}
                          className={`border p-1 text-center font-bold text-xs ${isCurrent && !dominantColor ? '' : dominantColor ? '' : 'bg-muted'}`}
                          data-testid={`total-${week}`}
                        >
                          {totalPlanejado > 0 ? (
                            <div className="font-mono">{totalPlanejado}</div>
                          ) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fitas Abertas */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-1 space-y-0 p-4 md:p-6">
          <div>
            <CardTitle className="text-lg md:text-2xl font-semibold">Fitas Abertas</CardTitle>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">{variedadeId !== "todas" ? `Fitas da variedade selecionada com ciclo ativo` : "Todas as fitas com ciclo ativo"} e o progresso de cada uma</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setLocation("/cacho/ciclos")} data-testid="button-view-all-cycles">
            Ver todos
            <ArrowUpRight className="h-4 w-4 ml-2" />
          </Button>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          {ciclosLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : !ciclos || getCiclosByVariedade(ciclos.filter(c => c.status === "ATIVO"), variedadeId, mangaras, setores).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhuma fita ativa no momento{variedadeId !== "todas" ? " para a variedade selecionada" : ""}
            </p>
          ) : (
            <>
              {/* Mobile: Cards View */}
              <div className="space-y-2 md:hidden">
                {sortCiclosByPrevisao(getCiclosByVariedade(ciclos.filter(c => c.status === "ATIVO"), variedadeId, mangaras, setores)).slice(0, 10).map((ciclo) => {
                  const status = getCicloStatus(ciclo);
                  const percentual = ciclo.totalPlanejado > 0 
                    ? Math.round(((ciclo.totalColhido + ciclo.totalPerdido) / ciclo.totalPlanejado) * 100) 
                    : 0;
                  const fitaInfo = getFitaInfo(ciclo, mangaras, coresFitas, setores, todasAsVariedades, variedadeId);
                  
                  return (
                    <div
                      key={ciclo.id}
                      className="flex flex-col gap-2 p-3 border rounded-lg bg-card hover-elevate cursor-pointer"
                      onClick={() => abrir(`/cacho/ciclos/${ciclo.id}`)}
                      data-testid={`ciclo-card-${ciclo.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1">
                          <div
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: fitaInfo.hexColor }}
                          />
                          <span className="text-xs font-medium">
                            {fitaInfo.corNome} - {fitaInfo.variedadeNome}
                            {fitaInfo.harvestWeek && ` - S${fitaInfo.harvestWeek}`}
                          </span>
                        </div>
                        <Badge variant={status.variant} className={`text-xs ${status.className || ""}`}>{status.label}</Badge>
                      </div>
                      
                      <div className="text-xs text-muted-foreground">
                        {fitaInfo.harvestDate && (
                          <>
                            {new Date(fitaInfo.harvestDate + 'T00:00:00Z').toLocaleDateString("pt-BR", { timeZone: 'UTC' })}
                          </>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Progress 
                          value={percentual} 
                          className={`h-1.5 flex-1 ${
                            percentual >= 90 ? "" :
                            percentual >= 70 ? "" :
                            "[&>div]:bg-destructive"
                          }`}
                        />
                        <span className="text-xs font-mono font-medium">{percentual}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cor/Fita</TableHead>
                      <TableHead>Previsão</TableHead>
                      <TableHead className="text-right">Planejado</TableHead>
                      <TableHead className="text-right">Colhido</TableHead>
                      <TableHead style={{ minWidth: "200px" }}>Progresso</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortCiclosByPrevisao(getCiclosByVariedade(ciclos.filter(c => c.status === "ATIVO"), variedadeId, mangaras, setores)).slice(0, 10).map((ciclo) => {
                      const status = getCicloStatus(ciclo);
                      const percentual = ciclo.totalPlanejado > 0 
                        ? Math.round(((ciclo.totalColhido + ciclo.totalPerdido) / ciclo.totalPlanejado) * 100) 
                        : 0;
                      const fitaInfo = getFitaInfo(ciclo, mangaras, coresFitas, setores, todasAsVariedades, variedadeId);
                      
                      return (
                        <TableRow 
                          key={ciclo.id} 
                          className="cursor-pointer hover-elevate"
                          onClick={() => abrir(`/cacho/ciclos/${ciclo.id}`)}
                          data-testid={`ciclo-row-${ciclo.id}`}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-4 h-4 rounded"
                                style={{ backgroundColor: fitaInfo.hexColor }}
                              />
                              <span>
                                {fitaInfo.corNome} - {fitaInfo.variedadeNome} - S{fitaInfo.harvestWeek}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {fitaInfo.harvestDate && (
                              <>({new Date(fitaInfo.harvestDate + 'T00:00:00Z').toLocaleDateString("pt-BR", { timeZone: 'UTC' })})</>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono">{ciclo.totalPlanejado}</TableCell>
                          <TableCell className="text-right font-mono">{ciclo.totalColhido}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress 
                                value={percentual} 
                                className={`h-2 flex-1 ${
                                  percentual >= 90 ? "" :
                                  percentual >= 70 ? "" :
                                  "[&>div]:bg-destructive"
                                }`}
                              />
                              <span className="text-xs font-mono font-medium w-10 text-right">{percentual}%</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={status.variant} className={status.className}>{status.label}</Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
